import {
  $computeTableMapSkipCellCheck,
  $createTableSelectionFrom,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  $isTableSelection,
  getTableElement,
  TableNode,
} from '@lexical/table';
import {$findMatchingParent, mergeRegister} from '@lexical/utils';
import tableStyles from 'components/DSYS/editor/themes/TableTheme.module.css';
import {
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  LexicalEditor,
  NodeKey,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {$isEnhancedTableNode} from 'plugins/enhancedTableCells';
import {useCallback, useEffect, useRef} from 'react';

type TableInfo = {
  tableElement: HTMLElement;
  tableNode: TableNode;
  tableKey: NodeKey;
  enableAddRows: boolean;
  enableAddColumns: boolean;
};

/**
 * Handles row and column selection via handles
 */
export function useTableSelectionHandlers(
  editor: LexicalEditor,
  tableInfo: TableInfo | null,
  setTableInfo: (info: TableInfo | null) => void,
  setSelectedCellKeys: (keys: Set<string>) => void
) {
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateTableInfo = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if (!$isTableSelection(selection) && !$isRangeSelection(selection)) {
        // Delay hiding the table info to prevent flashing
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          setTableInfo(null);
          setSelectedCellKeys(new Set());
        }, 50);
        return;
      }

      const anchorNode = selection.anchor.getNode();
      const tableCellNode = $findMatchingParent(anchorNode, $isTableCellNode);

      if (!tableCellNode) {
        // Delay hiding the table info to prevent flashing
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          setTableInfo(null);
          setSelectedCellKeys(new Set());
        }, 50);
        return;
      }

      // Clear any pending hide timeout since we're in a table
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableKey = tableNode.getKey();
      const tableElement = getTableElement(tableNode, editor.getElementByKey(tableKey));

      if (!tableElement) {
        // Delay hiding the table info to prevent flashing
        hideTimeoutRef.current = setTimeout(() => {
          setTableInfo(null);
          setSelectedCellKeys(new Set());
        }, 50);
        return;
      }

      // Get the table map for both selection tracking and settings check
      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

      // Track selected cells
      const selectedKeys = new Set<string>();

      if ($isTableSelection(selection)) {
        const shape = selection.getShape();

        // Iterate through the selection shape to find all selected cells
        for (let r = shape.fromY; r <= shape.toY; r++) {
          for (let c = shape.fromX; c <= shape.toX; c++) {
            const cellInfo = gridMap[r]?.[c];
            if (cellInfo?.cell) {
              selectedKeys.add(cellInfo.cell.getKey());
            }
          }
        }
      }
      setSelectedCellKeys(selectedKeys);

      // Check if adding rows/columns is enabled (not disabled) from the table node
      const enableAddRows = $isEnhancedTableNode(tableNode) ? tableNode.canAddRows() : true;
      const enableAddColumns = $isEnhancedTableNode(tableNode) ? tableNode.canAddColumns() : true;

      setTableInfo({
        tableElement,
        tableNode,
        tableKey,
        enableAddRows,
        enableAddColumns,
      });
    });
  }, [editor, setTableInfo, setSelectedCellKeys]);

  // Listen to selection changes
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTableInfo();
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerUpdateListener(() => {
        updateTableInfo();
      })
    );
  }, [editor, updateTableInfo]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Apply custom highlighting to only selected cells
  useEffect(() => {
    if (!tableInfo) return;

    const allCells = tableInfo.tableElement.querySelectorAll('th, td');

    // Determine which cells are selected and which are highlighted
    const cellsToHighlight = new Set<HTMLElement>();
    const highlightedCells = new Set<HTMLElement>();

    editor.getEditorState().read(() => {
      const tableNode = editor.getEditorState()._nodeMap.get(tableInfo.tableKey) as TableNode;
      if (!tableNode) return;

      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

      // Check which cells are selected
      const selectedKeys = new Set<string>();
      const selection = $getSelection();
      if ($isTableSelection(selection)) {
        const shape = selection.getShape();
        for (let r = shape.fromY; r <= shape.toY; r++) {
          for (let c = shape.fromX; c <= shape.toX; c++) {
            const cellInfo = gridMap[r]?.[c];
            if (cellInfo?.cell) {
              selectedKeys.add(cellInfo.cell.getKey());
            }
          }
        }
      }

      selectedKeys.forEach((key: string) => {
        const cellElement = editor.getElementByKey(key);
        if (cellElement) {
          cellsToHighlight.add(cellElement as HTMLElement);
        }
      });

      // Check which cells are highlighted (have background color from toolbar)
      for (let r = 0; r < gridMap.length; r++) {
        for (let c = 0; c < gridMap[r].length; c++) {
          const cellInfo = gridMap[r]?.[c];
          if (cellInfo?.cell && $isTableCellNode(cellInfo.cell)) {
            const cellElement = editor.getElementByKey(cellInfo.cell.getKey());
            if (cellElement) {
              // Check if cell has a background color set via Lexical (from toolbar highlighting)
              const bgColor = cellInfo.cell.getBackgroundColor();

              if (bgColor) {
                highlightedCells.add(cellElement as HTMLElement);
              }
            }
          }
        }
      }
    });

    // Apply selection state based on whether cell is already highlighted
    allCells.forEach((cell) => {
      const htmlCell = cell as HTMLElement;

      const isSelected = cellsToHighlight.has(htmlCell);
      const isAlreadyHighlighted = highlightedCells.has(htmlCell);

      // Remove all custom classes first
      htmlCell.classList.remove(tableStyles.selected, tableStyles.highlightedSelected);

      if (isSelected) {
        if (isAlreadyHighlighted) {
          htmlCell.classList.add(tableStyles.highlightedSelected);
        } else {
          htmlCell.classList.add(tableStyles.selected);
        }
      }
    });

    // Cleanup
    return () => {
      allCells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.classList.remove(tableStyles.selected, tableStyles.highlightedSelected);
      });
    };
  }, [tableInfo, editor]);

  const handleColumnClick = useCallback(
    (columnIndex: number, shiftKey: boolean) => {
      if (!tableInfo) return;

      editor.update(() => {
        const tableNode = editor.getEditorState()._nodeMap.get(tableInfo.tableKey) as TableNode;
        if (!tableNode) return;

        const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

        // Collect all unique cells in the column
        const seenCells = new Set<string>();
        const columnCells = [];

        for (let rowIndex = 0; rowIndex < tableMap.length; rowIndex++) {
          const cellInfo = tableMap[rowIndex]?.[columnIndex];
          if (cellInfo && cellInfo.cell) {
            const cellKey = cellInfo.cell.getKey();
            if (!seenCells.has(cellKey)) {
              seenCells.add(cellKey);
              columnCells.push(cellInfo.cell);
            }
          }
        }

        if (columnCells.length > 0) {
          const currentSelection = $getSelection();

          // If shift key is held and we have an existing table selection, extend it
          if (shiftKey && $isTableSelection(currentSelection)) {
            const currentNodes = currentSelection.getNodes();
            const allCells = [...currentNodes.filter($isTableCellNode), ...columnCells];

            // Remove duplicates
            const uniqueCellKeys = new Set<string>();
            const uniqueCells = allCells.filter((cell) => {
              const key = cell.getKey();
              if (uniqueCellKeys.has(key)) {
                return false;
              }
              uniqueCellKeys.add(key);
              return true;
            });

            if (uniqueCells.length > 0) {
              // Create a selection from the first to last cell in the combined set
              const firstCell = uniqueCells[0];
              const lastCell = uniqueCells[uniqueCells.length - 1];
              const selection = $createTableSelectionFrom(tableNode, firstCell, lastCell);
              $setSelection(selection);
            }
          } else {
            // Regular click: select just this column
            const firstCell = columnCells[0];
            const lastCell = columnCells[columnCells.length - 1];

            // Create a table selection from the first to last cell in the column
            const selection = $createTableSelectionFrom(tableNode, firstCell, lastCell);
            $setSelection(selection);
          }
          // State will be updated by updateTableInfo
        }
      });
    },
    [editor, tableInfo]
  );

  const handleRowClick = useCallback(
    (rowIndex: number, shiftKey: boolean) => {
      if (!tableInfo) return;

      editor.update(() => {
        const tableNode = editor.getEditorState()._nodeMap.get(tableInfo.tableKey) as TableNode;
        if (!tableNode) return;

        const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
        const rowMap = tableMap[rowIndex];

        if (!rowMap) return;

        // Collect all unique cells in the row
        const seenCells = new Set<string>();
        const rowCells = [];

        for (let colIndex = 0; colIndex < rowMap.length; colIndex++) {
          const cellInfo = rowMap[colIndex];
          if (cellInfo && cellInfo.cell) {
            const cellKey = cellInfo.cell.getKey();
            if (!seenCells.has(cellKey)) {
              seenCells.add(cellKey);
              rowCells.push(cellInfo.cell);
            }
          }
        }

        if (rowCells.length > 0) {
          const currentSelection = $getSelection();

          // If shift key is held and we have an existing table selection, extend it
          if (shiftKey && $isTableSelection(currentSelection)) {
            const currentNodes = currentSelection.getNodes();
            const allCells = [...currentNodes.filter($isTableCellNode), ...rowCells];

            // Remove duplicates
            const uniqueCellKeys = new Set<string>();
            const uniqueCells = allCells.filter((cell) => {
              const key = cell.getKey();
              if (uniqueCellKeys.has(key)) {
                return false;
              }
              uniqueCellKeys.add(key);
              return true;
            });

            if (uniqueCells.length > 0) {
              // Create a selection from the first to last cell in the combined set
              const firstCell = uniqueCells[0];
              const lastCell = uniqueCells[uniqueCells.length - 1];
              const selection = $createTableSelectionFrom(tableNode, firstCell, lastCell);
              $setSelection(selection);
            }
          } else {
            // Regular click: select just this row
            const firstCell = rowCells[0];
            const lastCell = rowCells[rowCells.length - 1];

            // Create a table selection from the first to last cell in the row
            const selection = $createTableSelectionFrom(tableNode, firstCell, lastCell);
            $setSelection(selection);
          }
          // State will be updated by updateTableInfo
        }
      });
    },
    [editor, tableInfo]
  );

  return {
    handleColumnClick,
    handleRowClick,
  };
}
